import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { CreateUserForm, CreateUserRequest, PagedResponse, User } from '../models/user.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

export interface UserPage {
  users: User[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly api = inject(ApiService);

  list(pageSize = 10, pageNumber = 1): Observable<User[]> {
    return this.listPage(pageNumber, pageSize).pipe(map((page) => page.users));
  }

  listPage(pageNumber = 1, pageSize = 10, tenantOwnersOnly = false, isActive?: boolean): Observable<UserPage> {
    return this.api
      .get<ApiResponse<User[] | PagedResponse<User>> | User[] | PagedResponse<User>>(
        API_ENDPOINTS.users,
        {
          search: '',
          pageNumber,
          pageSize,
          tenantOwnersOnly,
          ...(isActive === undefined ? {} : { isActive }),
        },
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => {
          const container = response as PagedResponse<User> & {
            users?: User[] | PagedResponse<User>;
          };
          const nested = Array.isArray(response) ? response : (container.users ?? container);
          const page = nested as PagedResponse<User>;
          const users: User[] = Array.isArray(nested)
            ? nested
            : (page.items ?? page.data ?? page.results ?? []);
          return {
            users: users.map((user) => this.toUser(user)),
            totalCount: Number(page.totalCount ?? users.length),
            pageNumber: Number(page.pageNumber ?? pageNumber),
            pageSize: Number(page.pageSize ?? pageSize),
          };
        }),
      );
  }

  invite(payload: CreateUserForm): Observable<User> {
    const [firstName, ...lastNameParts] = payload.name.trim().split(/\s+/);
    const body: CreateUserRequest = {
      email: payload.email,
      userName: payload.email.split('@')[0] || payload.email,
      password: 'Password@123',
      confirmPassword: 'Password@123',
      firstName: firstName || 'Staff',
      lastName: lastNameParts.join(' ') || 'User',
      emailConfirmed: true,
      isActive: true,
      roleIds: payload.roleIds,
      businessLocationIds: payload.businessLocationIds,
      accessExpiresAt: payload.accessExpiresAt || null,
      portal: payload.portal ?? 'business',
    };

    return this.api
      .post<ApiResponse<User> | User, CreateUserRequest>(API_ENDPOINTS.users, body, 'identity', 45_000)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => {
          const user = this.toUser('user' in response ? response.user : response, payload);
          return { ...user, invitationEmailSent: 'invitationEmailSent' in response ? Boolean(response.invitationEmailSent) : true };
        }),
      );
  }

  assignRole(userId: string, roleId: string): Observable<void> {
    return this.api
      .post<ApiResponse<void> | void, null>(
        `${API_ENDPOINTS.users}/${userId}/roles/${roleId}`,
        null,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  replaceRoles(userId: string, roleIds: string[]): Observable<string[]> {
    return this.api
      .put<ApiResponse<{ roleIds?: string[] }> | { roleIds?: string[] }, { roleIds: string[] }>(
        `${API_ENDPOINTS.users}/${userId}/roles`, { roleIds },
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.roleIds ?? roleIds),
      );
  }

  replaceBusinessLocations(userId: string, businessLocationIds: string[]): Observable<string[]> {
    return this.api
      .put<ApiResponse<{ businessLocationIds?: string[] }> | { businessLocationIds?: string[] }, { businessLocationIds: string[] }>(
        `${API_ENDPOINTS.users}/${userId}/business-locations`, { businessLocationIds },
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.businessLocationIds ?? businessLocationIds),
      );
  }

  assignMyBusinessLocation(locationId: string): Observable<string[]> {
    return this.api
      .post<ApiResponse<{ businessLocationIds?: string[] }> | { businessLocationIds?: string[] }, Record<string, never>>(
        `${API_ENDPOINTS.users}/me/business-locations/${locationId}`, {},
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.businessLocationIds ?? [locationId]),
      );
  }

  updateAccess(userId: string, isActive: boolean, accessExpiresAt: string | null): Observable<void> {
    return this.api
      .put<ApiResponse<void> | void, { isActive: boolean; accessExpiresAt: string | null }>(
        `${API_ENDPOINTS.users}/${userId}/access`, { isActive, accessExpiresAt },
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  resendInvite(userId: string): Observable<{ invitationEmailSent: boolean; invitationSentAt: string }> {
    return this.api
      .post<ApiResponse<{ invitationEmailSent: boolean; invitationSentAt: string }> | { invitationEmailSent: boolean; invitationSentAt: string }, Record<string, never>>(
        `${API_ENDPOINTS.users}/${userId}/resend-invite`, {}, 'identity', 45_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  remove(userId: string): Observable<void> {
    return this.api.delete<ApiResponse<void> | void>(`${API_ENDPOINTS.users}/${userId}`)
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  private toUser(value: unknown, fallback?: CreateUserForm): User {
    const user = (value ?? {}) as Record<string, unknown>;
    const firstName = typeof user['firstName'] === 'string' ? user['firstName'] : '';
    const lastName = typeof user['lastName'] === 'string' ? user['lastName'] : '';
    const roles = Array.isArray(user['roles']) ? user['roles'] : [];
    const role =
      typeof user['role'] === 'string'
        ? user['role']
        : typeof roles[0] === 'string'
          ? roles[0]
          : 'Unassigned';
    const roleIds = Array.isArray(user['roleIds'])
      ? user['roleIds'].filter((id): id is string => typeof id === 'string')
      : (fallback?.roleIds ?? []);
    const locationIds = Array.isArray(user['businessLocationIds'])
      ? user['businessLocationIds'].filter((id): id is string => typeof id === 'string')
      : (fallback?.businessLocationIds ?? []);

    return {
      id: String(user['id'] ?? ''),
      name: String(
        user['name'] ?? (`${firstName} ${lastName}`.trim() || fallback?.name || 'Unnamed user'),
      ),
      email: String(user['email'] ?? fallback?.email ?? ''),
      role,
      roles: roles.filter((value): value is string => typeof value === 'string'),
      roleIds,
      status: user['isActive'] === false ? 'Suspended' : String(user['status'] ?? 'Active'),
      business: String(user['business'] ?? user['businessName'] ?? ''),
      businessId: typeof user['tenantId'] === 'string' ? user['tenantId'] : undefined,
      businessLocationIds: locationIds,
      mustChangePassword: Boolean(user['mustChangePassword']),
      accessExpiresAt: typeof user['accessExpiresAt'] === 'string' ? user['accessExpiresAt'] : null,
      invitationSentAt: typeof user['invitationSentAt'] === 'string' ? user['invitationSentAt'] : null,
    };
  }
}
